import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import {
  Box,
  Button,
  colors,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ECTopbar } from './ECTopbar';
import { tokens } from '../../global/theme/tokens';
import DateTimeLabel from '../label/DateTimeLabel';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import ReplyIcon from '@mui/icons-material/Reply';
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import { useTranslation } from 'react-i18next';
import { ECSenderPreview } from './ECSenderPreview';
import CheckIcon from '@mui/icons-material/Check';
import { ECLinkSalesObject } from './ECLinkSalesObject';
import uuid from 'react-uuid';
import MediaPreview from '../exo/explorer/MediaPreview';
import MarqueeText from '../special/MarqueeText';
import SpeedDialMenu from '../menu/SpeedDialMenu';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FlagIcon from '@mui/icons-material/Flag';
import EmailIcon from '@mui/icons-material/Email';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import DraftsIcon from '@mui/icons-material/Drafts';
import { hasPermissions } from '../../auth/hasPermissions';

export const ECEmailConversation = forwardRef(
  ({ selectedEmail, onEdit, onChange, onChangeView, isMobile }, ref) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const config = useConfig();
    const { t } = useTranslation();

    const [apiData, setApiData] = useState(null);
    useEffect(() => {
      setApiData(null);
      if (selectedEmail)
        DBRequest({
          config,
          path: `imap-emails/${selectedEmail?.id}`,
          onResponse: handleResponse,
        });
    }, [config, selectedEmail]);

    function handleAction(type, oldEmail) {
      onEdit({ type, oldEmail });
    }

    function handleResponse(res) {
      setApiData(res);

      const { isSeen, id } = res.data;
      if (!isSeen)
        DBRequest({
          config,
          path: `imap-emails/${id}`,
          method: API_PATCH({ isSeen: true }),
          onResponse: (res) => {
            setApiData(res);
            onChange(res);
          },
        });
    }

    function updateEmail(email) {
      setApiData({ ...apiData, data: email });
    }

    // exposed functions
    useImperativeHandle(ref, () => ({
      updateEmail,
    }));

    function handleChange(res) {
      updateEmail(res.data);
      onChange(res);
    }

    const { id, isFlagged, salesObject, project, isSeen } = apiData?.data || {};

    function handleFlagClick() {
      DBRequest({
        config,
        path: `imap-emails/${id}`,
        method: API_PATCH({ isFlagged: !isFlagged }),
        onResponse: handleChange,
      });
    }

    function handleSeenClick() {
      DBRequest({
        config,
        path: `imap-emails/${id}`,
        method: API_PATCH({ isSeen: !isSeen }),
        onResponse: handleChange,
      });
    }

    return (
      <Box
        className="flex flex-col w-full h-full rounded-lg overflow-hidden backdrop-blur-lg"
        sx={{
          backgroundColor: colors.glass,
        }}
      >
        <ECTopbar>
          {apiData && (
            <Box className="flex h-full items-center justify-between w-full">
              {hasPermissions('sales-object', 'read') && (
                <ECLinkSalesObject
                  onChange={handleChange}
                  email={apiData.data}
                />
              )}
              <Box>
                <Tooltip title={t('Mark')}>
                  <IconButton
                    onClick={handleFlagClick}
                    color={isFlagged ? 'warning' : ''}
                  >
                    {isFlagged ? <FlagIcon /> : <OutlinedFlagIcon />}
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={handleSeenClick}
                  color={!isSeen ? 'info' : ''}
                >
                  {isSeen ? <DraftsIcon /> : <EmailIcon />}
                </IconButton>
              </Box>
            </Box>
          )}
        </ECTopbar>
        {isMobile && (
          <IconButton
            onClick={() => onChangeView('list')}
            sx={{
              position: 'absolute',
              bgcolor: colors.bgInput,
              height: '50px',
              width: '50px',
              borderRadius: '15px',
            }}
            className="bottom-2 left-2 z-10 backdrop-blur-md"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: '30px' }} />
          </IconButton>
        )}
        <Box className="w-full h-full flex flex-col overflow-y-auto gap-4 p-2">
          {apiData ? (
            <>
              <RenderEmail
                email={apiData.data}
                onAction={handleAction}
                onFlagClick={handleFlagClick}
              />
              {apiData.data.previousEmails?.map((email) => (
                <RenderEmail key={email.id} email={email} />
              ))}
            </>
          ) : (
            <Box className="w-full flex items-center justify-center pt-[12px]">
              <Typography className=" opacity-50">
                {t('No message selected')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

export const RenderEmail = ({ email, onAction, onFlagClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const {
    date,
    htmlBody,
    textBody,
    to,
    cc,
    sender,
    senderType,
    isSeen,
    isFlagged,
    id,
    attachments,
    subject,
  } = email;

  return (
    <Box sx={{ bgcolor: colors.card }} className="rounded-lg relative">
      <Box className="flex justify-between p-[3px]">
        <Box>
          <Typography fontWeight={600} className="pb-1 px-1">
            {subject}
          </Typography>
          {sender && (
            <ECSenderPreview
              sender={sender}
              type={senderType}
              avatarSize="47px"
              to={to}
              cc={cc}
            />
          )}
        </Box>

        <Box className="flex flex-col px-2">
          {onAction && (
            <Box className="flex">
              <IconButton size="small" onClick={() => onAction('reply', email)}>
                <ReplyIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onAction('replyAll', email)}
              >
                <ReplyAllIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onAction('forwarding', email)}
              >
                <ReplyIcon className=" -scale-x-100" />
              </IconButton>
            </Box>
          )}
          {date && (
            <Typography
              className="opacity-50 whitespace-nowrap"
              fontWeight={500}
              fontSize={11}
              textAlign="right"
            >
              <DateTimeLabel dateTime={date} useStrings />
            </Typography>
          )}
        </Box>
      </Box>
      {isFlagged && onFlagClick && (
        <Box>
          <Box
            sx={{ bgcolor: colors.warning + 30 }}
            className="flex gap-2 items-center px-1 py-[3px] justify-between"
          >
            <Box className="flex h-full items-center gap-2">
              <Box
                sx={{ bgcolor: colors.warning }}
                className="w-[5px] h-[15px] rounded-full"
              />
              <Typography sx={{ color: colors.warning }} fontSize={12}>
                {t('This message is marked for follow-up')}
              </Typography>
            </Box>
            <Button
              onClick={onFlagClick}
              variant="contained"
              color="success"
              disableElevation
              sx={{ padding: '0px 7px', fontSize: '11px' }}
            >
              {t('done')}
              <CheckIcon sx={{ fontSize: '14px' }} className="ml-[4px]" />
            </Button>
          </Box>
        </Box>
      )}

      <Box className="flex items-start w-full gap-2 flex-wrap px-4 py-1">
        {attachments?.map((att) => (
          <Attachment key={att.id} att={att} />
        ))}
      </Box>

      <Divider className="opacity-50" />
      <Box
        sx={{
          padding: '6px',
          iframe: {
            'span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div':
              {
                color: `${colors.text}!important`,
              },
          },
        }}
      >
        {htmlBody ? (
          <BodyHtmlEmailPreviewIframe htmlBody={htmlBody} />
        ) : (
          <BodyTextEmailPreviewIframe rawText={textBody} />
        )}
      </Box>
    </Box>
  );
};

const formatEmailText = (text) => {
  // Replace email links: hamm@domain<mailto:hamm@domain>
  text = text.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})<mailto:\1>/g,
    '<a href="mailto:$1">$1</a>'
  );

  // Replace web links: das-innovations.de<https://...>
  text = text.replace(
    /([\w.-]+\.[a-z]{2,})<https?:\/\/[^>]+>/gi,
    (match, name) => {
      const urlMatch = match.match(/<([^>]+)>/);
      if (urlMatch) {
        return `<a href="${urlMatch[1]}" target="_blank" rel="noopener noreferrer">${name}</a>`;
      }
      return name;
    }
  );

  // Replace \r\n or \n with <br />
  text = text.replace(/(\r\n|\r|\n)/g, '<br />');

  return text;
};

const BodyTextEmailPreviewIframe = ({ rawText }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const fontUrl = `${config?.apiUrl}/fonts/`;

  const iframeRef = useRef(null);
  const [height, setHeight] = useState('200px');

  const iframeId = uuid(); // unique per instance

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.data?.type === 'setHeight' &&
        typeof event.data.height === 'number' &&
        event.data.id === iframeId // only update if it's *this* iframe
      ) {
        setHeight(event.data.height + 40 + 'px');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeId]);

  const htmlBody = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body {
            font-family: montserrat;
            font-size: 14px;
            padding: 10px;
            margin: 0;
          }
          a {
            color: ${colors.info}!important;
            text-decoration: none;
          }
          a:hover {
            text-decoration: underline;
          }
          body, root, span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div {
            color: ${colors.text}!important;
            backgroundColor: transparent!important;
          }
          strong, strong>span, b, b>span{
            font-weight: 600!important;
          }
          *{
            margin: 0;
          }
        </style>
      </head>
      <body>
        ${formatEmailText(rawText)}
      <script>
          document.querySelectorAll('a').forEach(a => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
          });
          function sendHeight() {
            window.parent.postMessage({ type: 'setHeight', height: document.body.scrollHeight, id: '${iframeId}' }, '*');
          }
          window.addEventListener('load', sendHeight);
          const observer = new ResizeObserver(sendHeight);
          observer.observe(document.body);
        </script>
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      title="Email Preview"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      srcDoc={htmlBody}
      style={{ width: '100%', height, border: 'none' }}
    />
  );
};

export const BodyHtmlEmailPreviewIframe = ({
  htmlBody,
  disableColorManipulation,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const iframeRef = useRef(null);
  const [height, setHeight] = useState('200px');
  const config = useConfig();
  const fontUrl = `${config?.apiUrl}/fonts/`;
  const iframeId = uuid(); // unique per instance

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.data?.type === 'setHeight' &&
        typeof event.data.height === 'number' &&
        event.data.id === iframeId // only update if it's *this* iframe
      ) {
        setHeight(event.data.height + 40 + 'px');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeId]);

  const htmlWithEnhancements = `
    <!DOCTYPE html>
    <html>
      <head></head>
      <body>
      <style>
     ${
       !disableColorManipulation
         ? `a, a span {
            color: ${colors.info}!important;
            text-decoration: none;
          }
          a:hover {
            text-decoration: underline;
          }
          *, body, root, span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div {
            color: ${colors.text}!important;
          }
         *, body, table, div, td, tr, p, span  {
            background-color: transparent!important;
            max-width: 100%;
          }
          
     `
         : ''
     } 
          body {
            font-family: montserrat;
            font-size: 14px;
            padding: 10px;
            margin: 0;
            font-weight: 400;
          }
          strong, strong>span, b, b>span{
            font-weight: 500!important;
          }
          @font-face {
            font-family: 'Montserrat';
            src: url('${fontUrl}Montserrat.ttf') format('truetype');
            font-weight: 100 900;
            font-stretch: 75% 100%;
            font-style: normal;
          }
          @font-face {
            font-family: 'Montserrat';
            src: url('${fontUrl}Montserrat-Italic.ttf')
              format('truetype');
            font-weight: 100 900;
            font-stretch: 75% 100%;
            font-style: italic;
          }
          *{
            margin: 0;
          }    
     </style>
        ${htmlBody}
        <script>
          document.querySelectorAll('a').forEach(a => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
          });
          function sendHeight() {
            window.parent.postMessage({ type: 'setHeight', height: document.body.scrollHeight, id: '${iframeId}' }, '*');
          }
          window.addEventListener('load', sendHeight);
          const observer = new ResizeObserver(sendHeight);
          observer.observe(document.body);
        </script>
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      title="Email Preview"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      srcDoc={htmlWithEnhancements}
      style={{ width: '100%', height, border: 'none', overflow: 'hidden' }}
    />
  );
};

export const Attachment = ({ att }) => {
  const { t } = useTranslation();
  const actions = [
    {
      icon: <DownloadIcon />,
      name: t('Download'),
      onClick: () => {
        window.open(att.thumbnailUrl || att.temporaryUrl, '_blank');
      },
    },
  ];
  return (
    <Box className="flex gap-1 items-center">
      <MediaPreview
        src={att.thumbnailUrl || att.temporaryUrl}
        size="30px"
        mimeType={att.mimeType}
        allowPreview
      />
      <MarqueeText text={att.title} width="200px" />
      <SpeedDialMenu actions={actions} className=" z-10" noBlur />
    </Box>
  );
};
